<script lang="ts">
  import TermsModal from "./TermsModal.svelte";
  import { Encode } from "$shared/lib/query";
  import { replace } from "svelte-spa-router";
  import SpotClassPreview from "./SpotClassPreview.svelte";
  import PayPreview from "./PayPreview.svelte";
  import PermitDetailsForm from "./PermitDetailsForm.svelte";
  import api, {
    type ApiResponse,
    fromApi,
    intoModel,
  } from "$shared/lib/api-parkito2.svelte.ts";
  import SimplifiedDebtorSelector from "./SimplifiedDebtorSelector.svelte";
  import {
    DateModel,
    DebtorModel,
    IntervalModel,
    PermitPayPreviewModel,
    SpotModel,
  } from "$models";
  import {
    BodyText,
    Button,
    CardManager,
    ComfortCol,
    Header,
    Hor,
    Label,
    Page,
    Separator,
    Spacer,
    Toast,
    ValCheckbox,
    Ver,
    WithResource,
  } from "$components";
  import type {
    CreatePermitOutcome,
    Debtor,
    Interval,
    PaymentCard,
    PayPermitOutcome,
    Spot,
  } from "$apis/parkito2/index.ts";
  import { pop } from "svelte-spa-router";
  import { Decode } from "$shared/lib/query";
  import { myPages } from "../../stores/myPagesData.svelte.ts";
  import { derived, type Readable, writable } from "svelte/store";
  import { Form, Validate } from "$shared/lib/validation_next";

  const { params }: { params: { site_id: string } } = $props();

  const query = Decode.query({
    tags: Decode.array<string>,
    startDate: Decode.date("YYYY-MM-DD"),
    accessCode: Decode.optional(Decode.text),
  }).optional();

  const spotClassReq = fromApi(() =>
    query && query.tags
      ? api.spotclassGetSpots({
        body: { tagIds: query.tags, isGroup: true },
      })
        .then((resp): ApiResponse<SpotModel> => {
          if (!resp.data) {
            return resp;
          }

          if (resp.data.length === 0) {
            return {
              data: undefined,
              error: {
                message: "Hittade inga platser kopplade till platsklassen",
                type: "mapped",
              },
              request: undefined,
              response: undefined,
            };
          }

          if (resp.data.length > 1) {
            return {
              data: undefined,
              error: {
                message: "Hittade fler än en plats i platsklassen.",
                type: "mapped",
              },
              request: undefined,
              response: undefined,
            };
          }

          return {
            data: new SpotModel(resp.data[0]),
            error: undefined,
            request: undefined,
            response: undefined,
          };
        })
      : Promise.reject("No tag ids found in query")
  );
  $spotClassReq.load();
  const today = DateModel.today();

  const acceptEulaField = Form.checkbox(
    Validate.boolean().and(
      Validate.mustEqual(true, "Du måste godkänna villkoren"),
    ),
  );
  const hasCards = Form.checkbox(
    Validate.boolean().and(
      Validate.mustEqual(true, "Du måste lägga till ett betalkort"),
    ),
  );
  const hasDefaultCard = Form.checkbox(
    Validate.boolean().and(
      Validate.mustEqual(
        true,
        "Du måste välja ditt kort som betalkort genom att trycka på det",
      ),
    ),
  );

  const payForPermitValidation = Form.object({
    acceptEulaField,
    hasCards,
    hasDefaultCard,
  });

  let debtorSelectorComp: SimplifiedDebtorSelector | undefined = $state();
  const selectedDebtor = writable<Debtor | undefined>(undefined);

  const selectedDebtorId = derived(selectedDebtor, (sb) => sb?.id);

  const form = Form.input<{
    capacity: number;
    debtorId: string;
    licensePlate: string;
    startDate: DateModel;
    accessCode?: string | null;
    spotId: string;
    period?: Interval | null;
  }>(
    undefined,
    {
      capacity: 1,
      debtorId: "",
      licensePlate: "",
      startDate: query?.startDate ?? today,
      accessCode: query?.accessCode,
      spotId: "",
      period: null,
    },
  );

  function resetForm() {
    form.setValue({
      capacity: 1,
      debtorId: "",
      licensePlate: "",
      startDate: query?.startDate ?? today,
      accessCode: query?.accessCode,
      spotId: "",
      period: IntervalModel.fromMinutes(0)._data,
    });
  }

  const createPermitCall = fromApi((
    formValue: {
      accessCode?: string | null;
      debtorId: string;
      startDate: DateModel;
      licensePlate: string;
      capacity: number;
      period?: Interval | null;
    },
    spotId: string,
  ) =>
    api.createPermit({
      body: {
        accessCode: formValue.accessCode,
        debtorId: formValue.debtorId,
        startDate: formValue.startDate.format("YYYY-MM-DD"),
        initialLicensePlates: [formValue.licensePlate].filter((lp) =>
          lp !== "" && lp
        ) as string[],
        capacity: formValue.capacity,
        spotId,
      },
    })
  );

  const articlePayPreviewCall = fromApi((agreementId: string) =>
    api
      .previewPayPermit({ path: { agreement_id: agreementId } })
      .then(intoModel(PermitPayPreviewModel))
  );

  function getPermitCreateErrorToast(
    cpo: CreatePermitOutcome,
    options: { accessCodeName: string },
  ): string {
    switch (cpo.result) {
      case "ok":
        return "Allt gick bra";
      case "wouldExceedSpotCapacity":
        return "Denna parkering har blivit full. Prova att välja en annan annan parkering eller ett annat datum.";
      case "invalidAccessCode":
        return `Kunde inte verifiera er ${options.accessCodeName}. Dubbelkolla att du har fyllt i den rätt.`;
    }
  }
  async function createPermit(spot: SpotModel) {
    if ($form.res.isErr() || $form.res.value === undefined) {
      Toast.error("Fel i formulär: " + $form.res.error);
      return;
    }

    const formValue = $form.res.value;

    if (!spot.agreementPeriod) {
      Toast.error("Tillståndsplatsen saknar periodicitet");
      return;
    }

    const createPermitRes = await $createPermitCall.load(
      formValue,
      spot.id,
    );

    if (createPermitRes.error) {
      Toast.error("Något gick fel. Prova igen senare.");
      return;
    }

    if (createPermitRes.data.result === "ok") {
      document.getElementById("permit-details-form")?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
      await $articlePayPreviewCall.load(
        createPermitRes.data.details.agreement.id,
      );
    } else {
      $createPermitCall.reset();
      const errorMsg = getPermitCreateErrorToast(createPermitRes.data, {
        accessCodeName: spot.accessCodeRef?.name ?? "behörighetskod",
      });
      Toast.error(errorMsg, 10000);
    }
  }

  const payForPermitReq = fromApi((agreementId: string) =>
    api.payPermit({
      path: { agreement_id: agreementId },
    })
  );

  function getPayPermitErrorToast(
    outcome: PayPermitOutcome,
    options: { accessCodeName: string },
  ): string {
    switch (outcome.kind) {
      case "succeeded":
        return "Betalning lyckades";
      case "unknownPaymentError":
        return "Betalning misslyckades";
      case "accessCodeAtCapacity":
        return `Du får inte köpa fler tillstånd med den angivna koden`;
      case "insufficientFunds":
        return "Medges ej - medel saknas";
      case "cardDeclined":
        return "Medges ej";
    }
  }
  async function payForPermit(agreementId: string, spot: Spot) {
    const paymentReq = await $payForPermitReq.load(agreementId);
    if (paymentReq.error) {
      Toast.error("Något gick fel.");
    } else if (paymentReq.data.kind === "succeeded") {
      $myPages.reload();
      const q = Encode.asQuery({
        type: "permit",
        agreementId: agreementId,
      });
      replace(`/store/${params.site_id}/permit/checkout/success?${q}`);
    } else {
      const errorMsg = getPayPermitErrorToast(paymentReq.data, {
        accessCodeName: spot.accessCodeRef?.name ?? "behörighetskod",
      });
      Toast.error(errorMsg, 10000);
      return;
    }
  }
  let termsOpen = $state(false);

  const handleCardsLoaded = (cards: PaymentCard[]) => {
    if (cards.length > 0) {
      hasCards.input.set(true);
    }

    if (cards.find((c) => c.isDefault)) {
      hasDefaultCard.input.set(true);
    }
  };
</script>

<Page backButton={true} title="Dina uppgifter">
  {#snippet content()}
    <ComfortCol>
      <Ver>
        <WithResource api={myPages}>
          {#snippet child(myPagesData)}
            <SimplifiedDebtorSelector
              bind:this={debtorSelectorComp}
              myPages={myPagesData}
              {selectedDebtor}
              editable={!$createPermitCall.call.data}
              onDoneEditing={resetForm}
              reloadDebtors={$myPages.reload}
            />
          {/snippet}
        </WithResource>

        <WithResource api={spotClassReq}>
          {#snippet child(spotGroup)}
            {#if $selectedDebtor && !debtorSelectorComp?.isEditing()}
              <div>
                <div id="permit-details-form"></div>
                <PermitDetailsForm
                  selectedDebtor={new DebtorModel($selectedDebtor)}
                  {spotGroup}
                  preview={$createPermitCall.call.data?.result === "ok"}
                  formOutput={form}
                />
              </div>
              {#if query}
                {@const           selectedStartDate = $form.res.optional()?.startDate ||
            today}
                {#if selectedStartDate}
                  <SpotClassPreview
                    {spotGroup}
                    siteId={params.site_id}
                    startDate={selectedStartDate}
                    spotClassId={query.tags}
                  />
                {/if}
              {:else}
                <Label outset type="error">
                  Något har gått snett. Länken du har fått kan vara trasig
                </Label>
              {/if}
              {#if $createPermitCall.call.data?.result !== "ok"}
                <div class:invisible={$form.res.isOk()}>
                  <Label outset type="error">
                    {$form.res.error}
                  </Label>
                </div>
                <Hor>
                  <Button type="ghost" text="Tillbaka" onClick={pop}></Button>
                  <Spacer />
                  <Button
                    text="Till betalningen"
                    disabled={!$form.res.isOk()}
                    loading={$createPermitCall.isLoading()}
                    onClick={() => createPermit(spotGroup)}
                  />
                </Hor>
              {:else}
                <Separator />
                <Spacer minSpace="s"></Spacer>
                <Header size={"3"}>Ditt kort</Header>
                {@const           agreementId =
            $createPermitCall.call.data.details.agreement.id}
                {#if $selectedDebtorId}
                  <CardManager
                    onCardsFetched={handleCardsLoaded}
                    debtorId={selectedDebtorId as Readable<string>}
                  />
                {/if}
                <WithResource api={articlePayPreviewCall}>
                  {#snippet child(payPreview)}
                    <PayPreview
                      {payPreview}
                    />
                  {/snippet}
                </WithResource>
                <Separator />
                <Spacer minSpace="s" />
                <BodyText>Läs igenom och godkänn våra
                  <button
                    onclick={() => termsOpen = true}
                    class="underline"
                  >
                    villkor
                  </button>
                </BodyText>

                <Spacer minSpace="s" />

                {#if termsOpen}
                  <TermsModal
                    checkbox={acceptEulaField}
                    {agreementId}
                    onClose={() => termsOpen = false}
                  />
                {/if}
                <ValCheckbox
                  label="Jag har läst igenom och godkänner villkoren"
                  field={acceptEulaField}
                />
                <div class:invisible={$payForPermitValidation.res.isOk()}>
                  <Label outset type="error">
                    {$payForPermitValidation.res.error}
                  </Label>
                </div>
                <Hor wrap>
                  <Button
                    onClick={() => pop()}
                    text="Tillbaka"
                    type="ghost"
                  ></Button>
                  <Spacer />
                  <Button
                    loading={$payForPermitReq.isLoading()}
                    disabled={!$payForPermitValidation.res.isOk()}
                    onClick={() => payForPermit(agreementId, spotGroup)}
                    text="Betala"
                  ></Button>
                </Hor>
              {/if}
            {/if}
          {/snippet}
        </WithResource>
      </Ver>
    </ComfortCol>
  {/snippet}
</Page>
